import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';

import { cn } from '@/lib/utils';
import { approvalDocumentQueries } from '@/queries/approval-document/approval-document.queries';
import { fileMutations } from '@/queries/file/file.queries';

import { Button } from './button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from './dialog';

import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function getFileType(filename: string) {
	const ext = filename.toLowerCase().split('.').pop();
	if (!ext) return 'unknown';

	if (ext === 'pdf') return 'pdf';
	if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
	if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext))
		return 'office';

	return 'unsupported';
}

function Preview({
	url,
	name
}: Readonly<{ url: string, onApprove?: () => void, name: string }>) {
	const fileType = useMemo(() => getFileType(name), [url]);

	const [numPages, setNumPages] = useState<number>();
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [containerWidth, setContainerWidth] = useState<number>(0);

	const containerRef = useRef<HTMLDivElement>(null);

	function onDocumentLoadSuccess({
		numPages: nextNumPages
	}: {
		numPages: number
	}): void {
		setNumPages(nextNumPages);
		setPageNumber(1);
	}

	useEffect(() => {
		if (!containerRef.current) return;

		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (!entry) return;

			const newWidth = entry.contentRect.width;
			setContainerWidth(newWidth - 32);
		});

		observer.observe(containerRef.current);
		return () => observer.disconnect();
	}, []);

	const preview = useQuery({
		...approvalDocumentQueries.viewPdf(url),
		enabled: fileType === 'pdf'
	});

	const [objectUrl, setObjectUrl] = useState<string | null>(null);

	useEffect(() => {
		if (preview.data) {
			const url = URL.createObjectURL(preview.data);
			setObjectUrl(url);

			return () => URL.revokeObjectURL(url);
		}
	}, [preview.data]);

	function changePage(offset: number) {
		setPageNumber((prevPage) => {
			const newPage = prevPage + offset;
			return Math.min(Math.max(1, newPage), numPages ?? 1);
		});
	}

	if (fileType === 'pdf' && preview.isLoading) {
		return (
			<div className="flex h-full w-full items-center justify-center bg-gray-50 text-gray-400">
				Downloading Document...
			</div>
		);
	}

	if (fileType === 'pdf' && !preview.data) return null;

	return (
		<div
			className={cn(
				'relative flex min-h-0 w-full flex-1 flex-col rounded-lg bg-white',
				{
					'min-h-[75vh]': fileType === 'office'
				}
			)}
		>
			<div
				ref={containerRef}
				className="relative flex flex-1 justify-center overflow-x-hidden overflow-y-auto"
			>
				{fileType === 'pdf' && objectUrl && (
					<div className="flex h-full w-full justify-center overflow-x-hidden overflow-y-auto p-4">
						<Document
							file={objectUrl}
							onLoadSuccess={onDocumentLoadSuccess}
							className="my-2 shadow-lg transition-opacity duration-300"
							loading={
								<div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-50">
									<span className="text-gray-400">Parsing PDF...</span>
								</div>
							}
						>
							<Page
								pageNumber={pageNumber}
								width={containerWidth > 0 ? containerWidth : undefined}
								renderTextLayer={false}
								renderAnnotationLayer={false}
								className="h-auto w-full bg-white"
							/>
						</Document>
					</div>
				)}

				{fileType === 'image' && (
					<div className="flex h-full w-full items-center justify-center overflow-auto p-4">
						<img
							src={url}
							alt={name}
							className="max-h-full max-w-full object-contain shadow-sm"
						/>
					</div>
				)}

				{fileType === 'office' && (
					<div className="absolute inset-0 h-full w-full">
						<iframe
							src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`}
							className="h-full w-full border-none"
							title={name}
						/>
					</div>
				)}

				{fileType === 'unsupported' && (
					<div className="flex h-full w-full flex-col items-center justify-center text-gray-500">
						<p className="mb-4">
							Format file tidak dapat ditampilkan secara langsung.
						</p>
						<a
							href={url}
							download
							target="_blank"
							rel="noreferrer"
							className="bg-primary hover:bg-primary/90 flex items-center gap-2 rounded px-4 py-2 text-white"
						>
							<Download className="h-4 w-4" /> Unduh File
						</a>
					</div>
				)}
			</div>

			<div className="z-10 flex shrink-0 flex-col bg-white">
				{fileType === 'pdf' && (
					<div className="flex items-center justify-center gap-8 py-3">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => changePage(-1)}
							disabled={pageNumber <= 1}
						>
							<ChevronLeft className="mr-1 h-4 w-4" /> Previous
						</Button>

						<span className="text-sm font-semibold text-gray-700">
							Page {pageNumber} of {numPages || '--'}
						</span>

						<Button
							variant="ghost"
							size="sm"
							onClick={() => changePage(1)}
							disabled={!numPages || pageNumber >= numPages}
						>
							Next <ChevronRight className="ml-1 h-4 w-4" />
						</Button>
					</div>
				)}
			</div>
		</div>
	);
}

export default function PreviewFileDialog({
	fileKey,
	label
}: Readonly<{ fileKey: string, label: string }>) {
	const [openDoc, setOpenDoc] = useState(false);
	const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
	const fileMutation = useMutation(fileMutations.getPresign);

	return (
		<Dialog open={openDoc} onOpenChange={setOpenDoc}>
			<DialogTrigger asChild>
				<Button
					size={'sm'}
					className="h-auto w-full justify-start p-0 text-left whitespace-normal"
					type="button"
					variant={'link'}
					onClick={() => {
						fileMutation.mutate(
							{ key: fileKey },
							{
								onSuccess: ({ data: { url } }) => {
									setSelectedDocument(url);
									setOpenDoc(true);
								}
							}
						);
					}}
				>
					{label}
				</Button>
			</DialogTrigger>
			<DialogContent
				className="flex max-h-[85vh] flex-col overflow-hidden bg-white md:w-2/3 md:max-w-2/3"
				onPointerDownOutside={() => {
					setSelectedDocument(null);
				}}
				onEscapeKeyDown={() => {
					setSelectedDocument(null);
				}}
			>
				<DialogHeader>
					<DialogTitle className="flex shrink-0 flex-col gap-1">{label}</DialogTitle>
					<DialogDescription />
				</DialogHeader>
				{selectedDocument ? (
					<Preview url={selectedDocument} name={fileKey} />
				) : null}
			</DialogContent>
		</Dialog>
	);
}
